import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ComparisonSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    const section = document.querySelector('#comparison');
    if (section) observer.observe(section);
    
    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  const comparisonData = [
    {
      feature: t('comparison.speed'),
      tbcc: { value: t('comparison.speed.tbcc'), icon: <CheckCircle className="h-5 w-5 text-primary dark:text-primary-light" /> },
      dex: { value: t('comparison.speed.dex'), icon: <Clock className="h-5 w-5 text-yellow-400" /> },
      cex: { value: t('comparison.speed.cex'), icon: <CheckCircle className="h-5 w-5 text-green-400" /> }
    },
    {
      feature: t('comparison.anonymity'),
      tbcc: { value: t('comparison.anonymity.tbcc'), icon: <CheckCircle className="h-5 w-5 text-primary dark:text-primary-light" /> },
      dex: { value: t('comparison.anonymity.dex'), icon: <CheckCircle className="h-5 w-5 text-green-400" /> },
      cex: { value: t('comparison.anonymity.cex'), icon: <XCircle className="h-5 w-5 text-red-400" /> }
    },
    {
      feature: t('comparison.transparency'),
      tbcc: { value: t('comparison.transparency.tbcc'), icon: <CheckCircle className="h-5 w-5 text-primary dark:text-primary-light" /> },
      dex: { value: t('comparison.transparency.dex'), icon: <CheckCircle className="h-5 w-5 text-green-400" /> },
      cex: { value: t('comparison.transparency.cex'), icon: <XCircle className="h-5 w-5 text-red-400" /> }
    },
    {
      feature: t('comparison.ease'),
      tbcc: { value: t('comparison.ease.tbcc'), icon: <CheckCircle className="h-5 w-5 text-primary dark:text-primary-light" /> },
      dex: { value: t('comparison.ease.dex'), icon: <XCircle className="h-5 w-5 text-red-400" /> },
      cex: { value: t('comparison.ease.cex'), icon: <XCircle className="h-5 w-5 text-red-400" /> }
    },
    {
      feature: t('comparison.telegram'),
      tbcc: { value: t('comparison.telegram.tbcc'), icon: <CheckCircle className="h-5 w-5 text-primary dark:text-primary-light" /> },
      dex: { value: t('comparison.telegram.dex'), icon: <XCircle className="h-5 w-5 text-red-400" /> },
      cex: { value: t('comparison.telegram.cex'), icon: <XCircle className="h-5 w-5 text-red-400" /> }
    }
  ];

  return (
    <section 
      id="comparison" 
      className="py-24 relative bg-light-darker dark:bg-dark-lighter"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            {t('comparison.title')}
          </h2>
          <p className="max-w-3xl mx-auto text-gray-600 dark:text-gray-400 text-lg">
            {t('comparison.subtitle')}
          </p>
        </div>

        <div className={`relative overflow-x-auto gradient-border rounded-2xl transition-all duration-1000 shadow-xl shadow-primary/5 dark:shadow-primary/2 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <table className="w-full text-left">
            <thead className="bg-light-card dark:bg-dark-card border-b border-black/5 dark:border-white/5">
              <tr>
                <th className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-200">{t('comparison.feature')}</th>
                <th className="px-6 py-4 text-sm font-medium text-primary dark:text-primary-light">TBCC</th>
                <th className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-200">{t('comparison.traditional')}</th>
                <th className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-200">{t('comparison.centralized')}</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, index) => (
                <tr 
                  key={index} 
                  className={`border-b border-black/5 dark:border-white/5 transition-all duration-500 delay-${index * 100} hover:bg-light-card dark:hover:bg-dark-card`}
                >
                  <td className="px-6 py-4 font-medium text-black dark:text-white text-sm">{row.feature}</td>
                  <td className="px-6 py-4 bg-light-card dark:bg-dark-card">
                    <div className="flex items-center">
                      {row.tbcc.icon}
                      <span className="ml-2 text-primary dark:text-primary-light text-sm">{row.tbcc.value}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {row.dex.icon}
                      <span className="ml-2 text-gray-600 dark:text-gray-400 text-sm">{row.dex.value}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {row.cex.icon}
                      <span className="ml-2 text-gray-600 dark:text-gray-400 text-sm">{row.cex.value}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`mt-12 gradient-border blur-bg rounded-xl p-6 max-w-3xl mx-auto text-center transition-all duration-1000 delay-500 shadow-lg shadow-primary/5 dark:shadow-primary/2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-xl text-gray-900 dark:text-gray-200 font-medium">
            {t('comparison.conclusion')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;